import React from 'react';

import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { ButtonProps, notification } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import zipcelx, { ZipCelXSheet, ZipCelXDataSet } from 'zipcelx';

import { Button } from 'components/Button';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { CsvData, downloadAsCsv } from 'utils/csv';
import { captureAndShowError } from 'utils/error';
import { formatDate, formatNumber } from 'utils/formatting';
import { hasUserPermission } from 'utils/roleAndTenant';
import { track, TrackEvent } from 'utils/track';

export type GetDownloadDataFn = () => Promise<ZipCelXSheet | CsvData>;

export enum DownloadFileType {
  Excel = 'excel',
  Csv = 'csv',
}

export interface ExportButtonProps extends ButtonProps {
  getDownloadData: GetDownloadDataFn;
  recordType?: string;
  tooltipPlacement?: TooltipPlacement;
  permission?: TenantUserPermission;
  fileType?: DownloadFileType;
}

export async function notifyAndDownload(
  getDownloadData: GetDownloadDataFn,
  recordType?: string,
  fileType: DownloadFileType = DownloadFileType.Excel,
) {
  const key = 'downloadNotification';
  notification.info({
    key,
    message: 'Preparing Download',
    description: (
      <span>
        Loading records for download. <LoadingOutlined />
      </span>
    ),
    duration: 0,
  });

  try {
    const result = await getDownloadData();
    const fileName = `Recurrency_${recordType ? `${recordType}_` : ''}${formatDate(new Date().toLocaleDateString())}`;
    let recordCount = 0;
    let noRecordError = false;
    switch (fileType) {
      case DownloadFileType.Csv: {
        const resultWithType = result as CsvData;
        if (!resultWithType.rows || resultWithType.rows.length === 0) {
          noRecordError = true;
        } else {
          recordCount = resultWithType.rows.length;
          downloadAsCsv(resultWithType, fileName);
        }
        break;
      }
      case DownloadFileType.Excel: {
        const resultWithType = result as ZipCelXSheet;
        if (!resultWithType.data || resultWithType.data.length === 0) {
          noRecordError = true;
        } else {
          recordCount = resultWithType.data.length - 1; // exclude header row
          zipcelx({ filename: fileName, sheet: resultWithType });
        }
        break;
      }
      default:
        throw new Error('Unsupported download file type');
    }

    if (noRecordError) {
      throw new Error('No records available for download');
    }

    notification.success({
      key,
      message: 'Download Complete',
      description: `Downloaded ${formatNumber(recordCount)} records successfully.`,
    });
    track(TrackEvent.Components_ExportButton_Export, { recordCount, recordType });
  } catch (err) {
    captureAndShowError(err, `Error while download data`, { notificationKey: key });
  }
}

export function DownloadButton({
  getDownloadData,
  recordType,
  tooltipPlacement,
  permission,
  fileType = DownloadFileType.Excel,
  ...otherProps
}: ExportButtonProps) {
  const { activeTenant } = useGlobalApp();
  if (permission && !hasUserPermission(activeTenant, permission)) {
    return null;
  }
  return (
    <Tooltip
      title={fileType === DownloadFileType.Csv ? 'Download as CSV file' : 'Download as Excel file'}
      placement={tooltipPlacement}
    >
      <Button
        onClick={() => notifyAndDownload(getDownloadData, recordType, fileType)}
        icon={<DownloadOutlined />}
        {...otherProps}
      >
        Download
      </Button>
    </Tooltip>
  );
}

// Helper util functions for converting array to ZipCelXSheet
export interface DownloadXSheetColumn<RecordT> {
  title: string;
  type: 'string' | 'number';
  value: (record: RecordT) => string | number;
}

export function recordsToXSheet<RecordT>(records: RecordT[], columns: DownloadXSheetColumn<RecordT>[]): ZipCelXSheet {
  const data: ZipCelXDataSet = [
    columns.map((col) => ({ value: col.title, type: 'string' })), // header row
    ...records.map((record) => columns.map((col) => ({ value: col.value(record), type: col.type }))),
  ];
  return { data };
}
